import { TypographyProps, ContainerStyleProps, PaywallButton } from '@minecraft.atoms';
import { IconProps } from '@minecraft.icon';
import { ArtistSubscriptionFragment } from '@minecraft.graphql-operations';
import { gqlSubscription } from '@minecraft.graphql-types';
import { useModal } from '@minecraft.modal';
import { useEffect, useMemo } from 'react';
import { usePlans } from '../usePlans';
import { TranslateFn, useTranslation } from '../../I18N';
import { PaywallLocation } from '../enums/PaywallLocation';
import { BillingCodes } from '../enums/plans';
import { priceFormatter } from '../helpers/priceFormatter';

type ModalTheme = 'canadian' | 'default';
type ModalThemeValues = {
  offerText: string;
  highlightColor: TypographyProps['color'];
  headerIcon: string;
  bgColor: ContainerStyleProps['bgColor'];
  iconColor: IconProps['themeColor'];
  buttonColor?: React.ComponentProps<typeof PaywallButton>['color'];
  discountBadgeText?: string;
  discountBadgeIcon?: string;
};

const MODAL_THEMES: ({ t }: { t: TranslateFn }) => Record<ModalTheme, ModalThemeValues> = ({ t }) => ({
  canadian: {
    offerText: t('common:paywall.plan.special_offer_canada'),
    highlightColor: 'discountHighlightRed',
    headerIcon: 'canadaDiscount-asset',
    bgColor: 'discountOfferBgRed',
    iconColor: 'discountOfferRed',
    buttonColor: 'discountRed',
    discountBadgeText: t('common:paywall.plan.special_offer_canada_tagline'),
    discountBadgeIcon: 'canada-icon',
  },
  default: {
    offerText: t('common:paywall.plan.special_offer'),
    highlightColor: 'discountHighlight',
    headerIcon: 'discount-asset',
    bgColor: 'discountOfferBg',
    iconColor: 'discountOffer',
    buttonColor: 'discount',
  },
});

const SESSION_STORAGE_KEY = 'specialDiscountModalOpened';
const CANADA_CURRENCY_CODE = 'CAD';

export const useDiscountHook = ({
  artistId,
  subscription,
}: {
  subscription: ArtistSubscriptionFragment | gqlSubscription;
  artistId: string | number;
}) => {
  const specialDiscountModalOpened = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY)) || false;
  const { t } = useTranslation();

  const { plans } = usePlans({ subscription, artistId, paywallLocation: PaywallLocation.GLOBAL_NAV });
  const { isOpen: isDiscountModalOpen, open, toggleModal: toggleDiscountModal, close: closeDiscountModal } = useModal();

  const billingPlanCodes = plans?.map((plan) => plan.billingCode);
  const discountedPlanAnnual = plans?.find((plan) => plan.billingCode === BillingCodes.premiumAnnualAt1499);

  const originalPrice = priceFormatter({
    price: Number(discountedPlanAnnual?.strikeThroughPrice),
    t,
    roundingFloor: true,
  });
  const price = priceFormatter({
    price: Number(discountedPlanAnnual?.discountedPrice) / 12,
    currencyCode: discountedPlanAnnual?.currencyCode,
    t,
    roundingFloor: true,
  });
  const annualPrice = priceFormatter({
    price: Number(discountedPlanAnnual?.discountedPrice),
    t,
  });

  useEffect(() => {
    if (
      billingPlanCodes?.some((billingCode) =>
        [BillingCodes.premiumAnnualAt1499, BillingCodes.premiumMonthDiscount15].includes(billingCode as BillingCodes)
      ) &&
      !specialDiscountModalOpened
    ) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
      open();
    }
  }, [billingPlanCodes, specialDiscountModalOpened, open]);

  const isCanadianDiscount = discountedPlanAnnual?.currencyCode === CANADA_CURRENCY_CODE;
  const themeValues = useMemo(() => {
    const themes = MODAL_THEMES({ t });
    const activeTheme = isCanadianDiscount ? 'canadian' : 'default';
    return themes[activeTheme];
  }, [t, isCanadianDiscount]);

  const planFeatures = useMemo(
    () => [
      t('common:paywall.features.premium.storageUnlimited'),
      t('common:paywall.features.premium.trackSubmissions'),
      t('common:paywall.features.premium.connectSocial'),
    ],
    [t]
  );

  return useMemo(
    () => ({
      ...themeValues,
      isDiscountModalOpen,
      toggleDiscountModal,
      closeDiscountModal,
      planFeatures,
      originalPrice,
      price,
      annualPrice,
      discountedPlanAnnual,
    }),
    [
      isDiscountModalOpen,
      toggleDiscountModal,
      closeDiscountModal,
      themeValues,
      planFeatures,
      originalPrice,
      price,
      annualPrice,
      discountedPlanAnnual,
    ]
  );
};
